import {
  cloneElement,
  isValidElement,
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { DragEvent, JSX, ReactNode } from 'react';
import {
  Button,
  cn,
  IconChevronDown,
  IconCog,
  IconPlus,
  IconSearch,
  Input,
  ScreenLayout,
  SettingsPanel,
  SettingsRow,
  SettingsSection,
  SettingsSelect,
  SkeletonBlock,
  SkeletonText,
  Switch,
  usePref,
} from '@taki/ui';
import type { ScreenMenuItem } from '@taki/ui';
import {
  DOCK_APPS_MAX,
  DOCK_ICONS,
  iconRegistrySnapshot,
  loadIconRegistry,
  RegistryIcon,
  resolveDockApps,
  resolveDockIcon,
  resolveDockShortcuts,
} from '../Dock.js';
import type { DockShortcutPref, ResolvedDockShortcut } from '../Dock.js';
import type { ShellAppEntry } from '../types.js';
// [MENU DEV] Bloc temporaire, suppression : packages/shell/src/dev/README.md
import { useVisibleAppEntries } from '../dev/appVisibility.js';

/**
 * Hub Parametres du shell (route /settings), rendu dans le meme layout que
 * les apps (dock visible, surface pleine). Un onglet "Dock" (reglages du
 * dock) + un onglet PAR app installee : si l'app declare `loadSettings`, son
 * ecran de reglages est monte paresseusement ; sinon, etat vide standard.
 *
 * Exception assumee : les entrees du menu portent une icone (usage reserve au
 * hub, cf. ScreenMenuItem ; les apps n'en mettent jamais).
 */

/** Prefixe des ids de menu des apps : evite toute collision avec "dock". */
const APP_MENU_PREFIX = 'app:';

/** Icone du registre (size 22) reduite pour un item de menu du hub. */
function menuIconOf(entry: ShellAppEntry): ReactNode {
  return isValidElement(entry.icon)
    ? cloneElement(entry.icon as JSX.Element, { size: 15 })
    : entry.icon;
}

export interface SettingsHubScreenProps {
  /**
   * Apps installees, liste COMPLETE, dans l'ordre de `config.installedApps`.
   * Complete et non filtree : `DockSettingsPanel` ne fait pas qu'afficher cette
   * liste, il REECRIT les prefs du dock a partir d'elle.
   */
  appEntries: readonly ShellAppEntry[];
}

export function SettingsHubScreen({ appEntries }: SettingsHubScreenProps): JSX.Element {
  const [activeMenuId, setActiveMenuId] = useState('dock');

  // [MENU DEV] Le masquage ne porte QUE la barre d'onglets. `DockSettingsPanel`
  // garde la liste complete plus bas : il resout puis REECRIT `dock.apps` et
  // `dock.shortcuts` a partir de ce qu'il recoit, donc lui passer une liste
  // filtree effacerait pour de bon l'epinglage d'une app masquee. Le masquage
  // doit cacher, jamais detruire une autre preference.
  const menuAppEntries = useVisibleAppEntries(appEntries);

  const menuItems = useMemo<ScreenMenuItem[]>(
    () => [
      { id: 'dock', label: 'Dock', icon: <IconCog size={15} /> },
      ...menuAppEntries.map((entry) => ({
        id: APP_MENU_PREFIX + entry.id,
        label: entry.label,
        icon: menuIconOf(entry),
      })),
    ],
    [menuAppEntries],
  );

  // Un onglet peut disparaitre sous les pieds de l'utilisateur (app masquee
  // depuis le menu Home pose par-dessus le hub). Sans ce repli, `activeMenuId`
  // designerait un onglet absent : aucun onglet surligne, et la zone de contenu
  // annoncerait "Aucun reglage pour cette app" alors qu'aucune app n'est
  // choisie. Un repli calcule, jamais un useEffect qui corrige apres coup.
  const effectiveMenuId = menuItems.some((item) => item.id === activeMenuId)
    ? activeMenuId
    : 'dock';

  const activeEntry = useMemo(
    () => appEntries.find((entry) => APP_MENU_PREFIX + entry.id === effectiveMenuId),
    [appEntries, effectiveMenuId],
  );

  // Memoise par entree : recreer le composant lazy a chaque rendu
  // demonterait/remonterait l'ecran de reglages (perte d'etat), comme pour la
  // route d'app du shell.
  const LazySettings = useMemo(
    () => (activeEntry?.loadSettings != null ? lazy(activeEntry.loadSettings) : null),
    [activeEntry],
  );

  let zone5: ReactNode = null;
  let emptySettings = false;
  if (effectiveMenuId === 'dock') {
    zone5 = <DockSettingsPanel appEntries={appEntries} />;
  } else if (LazySettings != null) {
    // Le settings.tsx de l'app est charge paresseusement : petit squelette
    // (l'en-tete de section + lignes de texte) plutot qu'un vide, le temps que
    // le chunk arrive. Titre = le nom de l'app, deja connu.
    // La COLONNE BORNEE (42rem) appartient a SettingsPanel ; tant qu'un
    // settings.tsx d'app pose encore son propre cadre pleine largeur, le hub
    // borne ici, pour que tous les onglets partagent la meme colonne. Quand
    // toutes les apps auront adopte SettingsPanel, cet habillage devient un
    // 42rem dans un 42rem (inoffensif) et pourra tomber.
    zone5 = (
      <div className="max-w-[42rem]">
        <Suspense fallback={<SettingsLazyFallback title={activeEntry?.label ?? 'Reglages'} />}>
          <LazySettings />
        </Suspense>
      </div>
    );
  } else {
    // App sans `loadSettings` : etat vide standard du gabarit.
    emptySettings = true;
  }

  return (
    <ScreenLayout
      appName="Parametres"
      appIcon={<IconCog size={18} />}
      menuItems={menuItems}
      activeMenuId={effectiveMenuId}
      onMenuSelect={setActiveMenuId}
      title="Parametres"
      empty={emptySettings}
      emptyTitle="Aucun reglage pour cette app"
    >
      {zone5}
    </ScreenLayout>
  );
}

/**
 * Squelette d'attente pendant le chargement paresseux d'un `settings.tsx` :
 * l'en-tete d'une section + quelques lignes de texte, dans le MEME cadre que
 * les vrais panneaux (`SettingsPanel`, kit v2.2) : a l'arrivee du chunk, le
 * titre de section ne bouge pas d'un rem.
 */
function SettingsLazyFallback({ title }: { title: string }): JSX.Element {
  return (
    <SettingsPanel>
      <SettingsSection title={title}>
        <SkeletonText lines={3} />
      </SettingsSection>
    </SettingsPanel>
  );
}

/**
 * Squelette de la grille "Toutes les icones" pendant le chargement (import
 * dynamique) du registre complet : reproduit le cadre borne et les pastilles
 * 2.25rem (h-9/w-9) de la vraie grille, sans defilement.
 */
function IconGridSkeleton(): JSX.Element {
  return (
    <div
      aria-hidden
      className="max-h-[15rem] overflow-hidden rounded-md border border-border-soft p-1.5"
    >
      <div className="flex flex-wrap gap-1.5">
        {Array.from({ length: 24 }, (_, index) => (
          <SkeletonBlock key={index} w="2.25rem" h="2.25rem" rounded="0.625rem" />
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Onglet Dock : apps mises en avant + raccourcis d'action                     */
/* -------------------------------------------------------------------------- */

/** Action declaree, aplatie avec son app (options du select d'ajout). */
interface FlatAction {
  appId: string;
  appLabel: string;
  actionId: string;
  actionLabel: string;
  iconName: string;
}

/** Props de drag a etaler sur une ligne reordonnables. */
interface RowDragProps {
  draggable: true;
  onDragStart: (event: DragEvent) => void;
  onDragOver: (event: DragEvent) => void;
  onDrop: (event: DragEvent) => void;
  onDragEnd: () => void;
}

interface RowDrag {
  /** Props a etaler sur la ligne d'index donne. */
  rowProps: (index: number) => RowDragProps;
  /** Index de la ligne saisie (opacity reduite), sinon null. */
  dragIndex: number | null;
  /** Index de la ligne survolee pendant un drag (liseret lime), sinon null. */
  overIndex: number | null;
}

/**
 * Drag and drop natif d'une liste de lignes : suit l'index saisi et la cible
 * survolee (pour le feedback visuel) et delegue le deplacement a `onMove`.
 */
function useRowDrag(onMove: (from: number, to: number) => void): RowDrag {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  function rowProps(index: number): RowDragProps {
    return {
      draggable: true,
      onDragStart: (event) => {
        setDragIndex(index);
        event.dataTransfer.effectAllowed = 'move';
        // Firefox exige une donnee pour demarrer le drag.
        event.dataTransfer.setData('text/plain', String(index));
      },
      onDragOver: (event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        setOverIndex(index);
      },
      onDrop: (event) => {
        event.preventDefault();
        if (dragIndex != null && dragIndex !== index) onMove(dragIndex, index);
        setDragIndex(null);
        setOverIndex(null);
      },
      onDragEnd: () => {
        setDragIndex(null);
        setOverIndex(null);
      },
    };
  }

  return { rowProps, dragIndex, overIndex };
}

/** Classes d'une ligne reordonnables, feedback de drag inclus. */
function cnDragRow(drag: RowDrag, index: number): string {
  return cn(
    'flex min-h-[2.75rem] items-center gap-3 rounded-md px-1',
    drag.dragIndex === index && 'opacity-40',
    drag.overIndex === index &&
      drag.dragIndex != null &&
      drag.dragIndex !== index &&
      'ring-1 ring-inset ring-accent-border',
  );
}

/** Poignee de prise : trois barres, decouvrable au premier regard. */
function GripHandle(): JSX.Element {
  return (
    <span
      aria-hidden
      title="Glisser pour reordonner"
      className={cn(
        'flex h-6 w-4 shrink-0 cursor-grab flex-col items-center justify-center gap-[0.1875rem]',
        'text-ink-300 active:cursor-grabbing',
      )}
    >
      <span className="h-px w-3 bg-current" />
      <span className="h-px w-3 bg-current" />
      <span className="h-px w-3 bg-current" />
    </span>
  );
}

/** Fleches monter/descendre : l'alternative accessible au drag. */
function MoveButtons({
  index,
  count,
  onMove,
  label,
}: {
  index: number;
  count: number;
  onMove: (from: number, to: number) => void;
  label: string;
}): JSX.Element {
  const buttonClasses = cn(
    'flex h-6 w-6 items-center justify-center rounded-md text-ink-400 transition-colors',
    'hover:bg-surface-1 hover:text-ink-900 disabled:pointer-events-none disabled:opacity-30',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border',
  );
  return (
    <span className="flex shrink-0 items-center">
      <button
        type="button"
        aria-label={`Monter ${label}`}
        disabled={index === 0}
        onClick={() => onMove(index, index - 1)}
        className={buttonClasses}
      >
        <IconChevronDown size={14} className="rotate-180" />
      </button>
      <button
        type="button"
        aria-label={`Descendre ${label}`}
        disabled={index === count - 1}
        onClick={() => onMove(index, index + 1)}
        className={buttonClasses}
      >
        <IconChevronDown size={14} />
      </button>
    </span>
  );
}

/** Ligne d'aide discrete au-dessus d'une liste reordonnables. */
function ReorderHelp(): JSX.Element {
  return (
    <p className="text-xs text-ink-400">
      Glisse les lignes ou utilise les fleches pour changer l&apos;ordre.
    </p>
  );
}

/**
 * Reglages du dock, dans le cadre SettingsPanel (@taki/ui, colonne bornee a
 * 42rem), en deux groupes SettingsSection :
 *   (a) Apps mises en avant : liste des apps installees, reordonnancement par
 *       drag and drop natif (poignee + fleches), interrupteur de presence
 *       (Switch, verrouille avec sa raison quand le plafond est atteint),
 *       plafond 6. Ecrit "dock.apps".
 *   (b) Raccourcis d'action : liste configuree (drag and drop, retrait) +
 *       bloc d'ajout (select des actions declarees, grille DOCK_ICONS +
 *       picker complet du registre a la demande). Ecrit "dock.shortcuts".
 */
function DockSettingsPanel({
  appEntries,
}: {
  appEntries: readonly ShellAppEntry[];
}): JSX.Element {
  const entriesById = useMemo(
    () => new Map(appEntries.map((entry) => [entry.id, entry])),
    [appEntries],
  );

  /* ---- (a) Apps mises en avant ---- */

  const [appsPref, setAppsPref] = usePref<string[] | null>('dock.apps', null);
  const selectedIds = useMemo(
    () => resolveDockApps(appsPref, appEntries).map((entry) => entry.id),
    [appsPref, appEntries],
  );
  const selectedSet = new Set(selectedIds);

  // Ordre d'affichage local de la liste complete : les apps du dock d'abord
  // (ordre de la pref), puis les autres (ordre du registre). Seul l'ordre des
  // apps COCHEES est persiste (c'est lui que le dock affiche).
  const [order, setOrder] = useState<string[]>(() => {
    const selected = resolveDockApps(appsPref, appEntries).map((entry) => entry.id);
    const rest = appEntries.map((entry) => entry.id).filter((id) => !selected.includes(id));
    return [...selected, ...rest];
  });

  function writeSelection(nextOrder: string[], nextSelected: Set<string>): void {
    setAppsPref(nextOrder.filter((id) => nextSelected.has(id)));
  }

  function moveApp(from: number, to: number): void {
    const next = [...order];
    const [moved] = next.splice(from, 1);
    if (moved == null) return;
    next.splice(to, 0, moved);
    setOrder(next);
    writeSelection(next, selectedSet);
  }

  function toggleApp(id: string): void {
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id);
    else {
      if (next.size >= DOCK_APPS_MAX) return;
      next.add(id);
    }
    writeSelection(order, next);
  }

  const appsDrag = useRowDrag(moveApp);

  /* ---- (b) Raccourcis d'action ---- */

  const [shortcutsPref, setShortcutsPref] = usePref<DockShortcutPref[] | null>(
    'dock.shortcuts',
    null,
  );
  const shortcuts = useMemo(
    () => resolveDockShortcuts(shortcutsPref, appEntries),
    [shortcutsPref, appEntries],
  );

  function writeShortcuts(next: readonly ResolvedDockShortcut[]): void {
    setShortcutsPref(
      next.map(({ appId, actionId, iconName }) => ({ appId, actionId, iconName })),
    );
  }

  function moveShortcut(from: number, to: number): void {
    const next = [...shortcuts];
    const [moved] = next.splice(from, 1);
    if (moved == null) return;
    next.splice(to, 0, moved);
    writeShortcuts(next);
  }

  function removeShortcut(index: number): void {
    writeShortcuts(shortcuts.filter((_, i) => i !== index));
  }

  const shortcutsDrag = useRowDrag(moveShortcut);

  // Toutes les actions declarees par les apps installees, aplaties pour le
  // select d'ajout (libelle "<App> : <Action>").
  const allActions = useMemo<FlatAction[]>(
    () =>
      appEntries.flatMap((entry) =>
        (entry.actions ?? []).map((action) => ({
          appId: entry.id,
          appLabel: entry.label,
          actionId: action.id,
          actionLabel: action.label,
          iconName: action.iconName,
        })),
      ),
    [appEntries],
  );

  // Bloc d'ajout : index (stringifie) de l'action choisie + icone choisie
  // (nom PascalCase de DOCK_ICONS ou clef kebab du registre complet).
  const [pickedAction, setPickedAction] = useState('');
  const [pickedIcon, setPickedIcon] = useState('IconModule');

  function addShortcut(): void {
    const item = allActions[Number(pickedAction)];
    if (item == null) return;
    writeShortcuts([
      ...shortcuts,
      {
        appId: item.appId,
        appLabel: item.appLabel,
        actionId: item.actionId,
        actionLabel: item.actionLabel,
        iconName: pickedIcon,
      },
    ]);
    setPickedAction('');
  }

  /* ---- Registre complet des icones (picker "Toutes les icones") ---- */

  const [allIconsOpen, setAllIconsOpen] = useState(false);
  const [iconQuery, setIconQuery] = useState('');
  // Le registre (~centaines de KB) n'arrive QUE par import() dynamique : a
  // l'ouverture du picker complet, ou si un raccourci configure porte deja
  // une clef kebab (pour rendre son icone dans la liste).
  const [iconRegistry, setIconRegistry] = useState(iconRegistrySnapshot);
  const needsIconRegistry =
    allIconsOpen || shortcuts.some((shortcut) => DOCK_ICONS[shortcut.iconName] == null);
  useEffect(() => {
    if (!needsIconRegistry || iconRegistry != null) return;
    let cancelled = false;
    void loadIconRegistry().then((registry) => {
      if (!cancelled) setIconRegistry(registry);
    });
    return () => {
      cancelled = true;
    };
  }, [needsIconRegistry, iconRegistry]);

  const filteredIconNames = useMemo(() => {
    if (iconRegistry == null) return [];
    const query = iconQuery.trim().toLowerCase();
    const names = Object.keys(iconRegistry);
    return query === '' ? names : names.filter((name) => name.includes(query));
  }, [iconRegistry, iconQuery]);

  /** Classes d'une pastille d'icone selectionnable (grilles du bloc d'ajout). */
  function cnIconChoice(picked: boolean): string {
    return cn(
      'flex h-9 w-9 items-center justify-center rounded-md border transition-colors',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border',
      picked
        ? 'border-accent-border bg-accent-tint text-accent-ink'
        : 'border-border bg-surface text-ink-600 hover:bg-surface-1',
    );
  }

  return (
    <SettingsPanel>
      {/* ---------- (a) Apps mises en avant ---------- */}
      <SettingsSection
        title="Apps mises en avant"
        description={`Jusqu'a ${DOCK_APPS_MAX} apps epinglees dans le dock, dans cet ordre.`}
      >
        {/* Les rangees reordonnables ne sont pas des SettingsRow : elles
            portent leur propre rythme (interligne 0.25rem), pose ici. */}
        <div className="mt-1 flex flex-col gap-1">
          <ReorderHelp />
          {order.map((id, index) => {
            const entry = entriesById.get(id);
            if (entry == null) return null;
            const checked = selectedSet.has(id);
            const capped = !checked && selectedSet.size >= DOCK_APPS_MAX;
            return (
              <div key={id} {...appsDrag.rowProps(index)} className={cnDragRow(appsDrag, index)}>
                <GripHandle />
                <Switch
                  checked={checked}
                  locked={capped}
                  lockedReason={`Dock plein (${DOCK_APPS_MAX} max) : retire une app pour en epingler une autre.`}
                  onChange={() => toggleApp(id)}
                  aria-label={`Afficher ${entry.label} dans le dock`}
                />
                <span className="flex w-6 shrink-0 items-center justify-center text-ink-600">
                  {isValidElement(entry.icon)
                    ? cloneElement(entry.icon as JSX.Element, { size: 17 })
                    : entry.icon}
                </span>
                <span
                  className={cn(
                    'min-w-0 flex-1 truncate text-sm',
                    checked ? 'font-medium text-ink-900' : 'text-ink-500',
                  )}
                >
                  {entry.label}
                </span>
                {capped && (
                  <span className="shrink-0 text-[0.6875rem] text-ink-400">
                    dock plein ({DOCK_APPS_MAX} max)
                  </span>
                )}
                <MoveButtons index={index} count={order.length} onMove={moveApp} label={entry.label} />
              </div>
            );
          })}
        </div>
      </SettingsSection>

      {/* ---------- (b) Raccourcis d'action ---------- */}
      <SettingsSection
        title="Raccourcis d'action"
        description="Actions rapides epinglees sous les apps du dock."
      >
        {/* Raccourcis configures */}
        {shortcuts.length > 0 ? (
          <div className="mt-1 flex flex-col gap-1">
            <ReorderHelp />
            {shortcuts.map((shortcut, index) => {
              const Icon = resolveDockIcon(shortcut.iconName, iconRegistry);
              const label = `${shortcut.appLabel} : ${shortcut.actionLabel}`;
              return (
                <div
                  key={`${shortcut.appId}:${shortcut.actionId}:${index}`}
                  {...shortcutsDrag.rowProps(index)}
                  className={cnDragRow(shortcutsDrag, index)}
                >
                  <GripHandle />
                  <span className="flex w-6 shrink-0 items-center justify-center text-ink-600">
                    <Icon size={17} />
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink-900">
                    {label}
                  </span>
                  <MoveButtons
                    index={index}
                    count={shortcuts.length}
                    onMove={moveShortcut}
                    label={label}
                  />
                  <Button variant="ghost" size="sm" onClick={() => removeShortcut(index)}>
                    Retirer
                  </Button>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="mt-1 text-[0.8125rem] text-ink-400">Aucun raccourci configure.</p>
        )}

        {/* Bloc d'ajout */}
        {allActions.length > 0 ? (
          <div className="mt-4 flex flex-col gap-1">
            <span className="text-[0.8125rem] font-medium text-ink-700">
              Ajouter un raccourci
            </span>

            <SettingsRow label="Action" hint="Le geste que le raccourci declenche">
              <SettingsSelect
                value={pickedAction}
                onChange={(event) => setPickedAction(event.target.value)}
                aria-label="Action a epingler"
              >
                <option value="">Choisir une action</option>
                {allActions.map((item, index) => (
                  <option key={`${item.appId}:${item.actionId}`} value={String(index)}>
                    {`${item.appLabel} : ${item.actionLabel}`}
                  </option>
                ))}
              </SettingsSelect>
            </SettingsRow>

            {/* Icone : la grille curatee DOCK_ICONS, puis le registre complet
                a la demande. */}
            <div className="flex flex-col gap-2">
              <span className="text-xs text-ink-400">Icone du raccourci</span>
              <div className="flex flex-wrap gap-1.5" role="group" aria-label="Icone du raccourci">
                {Object.entries(DOCK_ICONS).map(([name, Icon]) => (
                  <button
                    key={name}
                    type="button"
                    title={name}
                    aria-pressed={name === pickedIcon}
                    onClick={() => setPickedIcon(name)}
                    className={cnIconChoice(name === pickedIcon)}
                  >
                    <Icon size={17} />
                  </button>
                ))}
              </div>

              {!allIconsOpen ? (
                <div>
                  <Button variant="ghost" size="sm" onClick={() => setAllIconsOpen(true)}>
                    Toutes les icones
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <Input
                    leftIcon={<IconSearch size={16} />}
                    value={iconQuery}
                    onChange={(event) => setIconQuery(event.target.value)}
                    placeholder="Rechercher une icone"
                    aria-label="Rechercher une icone"
                    className="max-w-[20rem]"
                  />
                  {iconRegistry == null ? (
                    <IconGridSkeleton />
                  ) : filteredIconNames.length === 0 ? (
                    <p className="text-[0.8125rem] text-ink-400">
                      Aucune icone ne correspond a la recherche.
                    </p>
                  ) : (
                    /* Grille scrollable BORNEE : exception assumee dans un
                       panneau de reglages (pas une zone 5 d'app). */
                    <div className="max-h-[15rem] overflow-y-auto rounded-md border border-border-soft p-1.5">
                      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Toutes les icones">
                        {filteredIconNames.map((name) => {
                          const inner = iconRegistry[name];
                          if (inner == null) return null;
                          return (
                            <button
                              key={name}
                              type="button"
                              title={name}
                              aria-pressed={name === pickedIcon}
                              onClick={() => setPickedIcon(name)}
                              className={cnIconChoice(name === pickedIcon)}
                            >
                              <RegistryIcon inner={inner} size={17} />
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="mt-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={pickedAction === ''}
                onClick={addShortcut}
              >
                <IconPlus size={15} />
                Ajouter
              </Button>
            </div>
          </div>
        ) : (
          <p className="mt-1 text-[0.8125rem] text-ink-400">
            Aucune app ne declare encore d&apos;action : le dock garde ses raccourcis par defaut.
          </p>
        )}
      </SettingsSection>
    </SettingsPanel>
  );
}
